/** PROTOTYPE module detail pane (per spread) -- THROWAWAY. Matches the production master/detail. */
import React, { useState } from 'react';
import { CRC } from './moduleInputsData';
import { Label, NumberInput, Segmented, Toggle } from './protoAtoms';
import {
  Spread, ModuleKey, MACHINE_KEYS, MACHINE_LABELS, MODULE_LABELS,
  SpreadPricing, moduleVolume, moduleSources, money, joints,
} from './spreadSplitData';
import { ModuleSource } from './spreadSourceData';
import { MtOptions, MT_ADDONS } from './spreadMaterialData';
import { SpreadWeldingLive } from './SpreadWeldingLive';
import { SpreadMaterialInputs } from './SpreadMaterialInputs';
import { SpreadSourceInputs } from './SpreadSourceInputs';
import { SpreadModuleLive } from './SpreadModuleLive';
import { SpreadProjectLevelLive } from './SpreadProjectLevelLive';
import { SpreadProjectOptionsCard } from './SpreadProjectOptionsCard';
import { PlatformOptions, RetentionOptions } from './spreadProjectOptions';
import { SpreadModal } from './SpreadModal';
import { SpreadPriceState } from './useSpreadPrice';
import { WeldMode } from './spreadPricingApi';

type SourceModule = 'mt' | 'ndt' | 'coating';
const SOURCE_META: Record<SourceModule, { noun: string; countLabel: string; unit: string }> = {
  mt: { noun: 'Mill', countLabel: 'Pipe count', unit: 'pipes' },
  ndt: { noun: 'Vendor', countLabel: 'Inspections', unit: 'inspections' },
  coating: { noun: 'Contractor', countLabel: 'Joints', unit: 'joints' },
};

interface Props {
  spread: Spread;
  moduleKey: ModuleKey;
  pricing: SpreadPricing;
  setMachine: (k: typeof MACHINE_KEYS[number], v: number) => void;
  setMtMills: (mills: ModuleSource[]) => void;
  setMtOptions: (options: MtOptions) => void;
  setNdtSources: (sources: ModuleSource[]) => void;
  setCoatingSources: (sources: ModuleSource[]) => void;
  setPlatformOptions: (o: PlatformOptions) => void;
  setRetentionOptions: (o: RetentionOptions) => void;
  setEnabled: (key: ModuleKey, on: boolean) => void;
  onEditOverrides: () => void;
  spreadState: SpreadPriceState;
  weldMode: WeldMode;
  setWeldMode: (m: WeldMode) => void;
  mobilizing: boolean;
  setMobilizing: (v: boolean) => void;
}

const card: React.CSSProperties = { background: '#fff', border: `1px solid ${CRC.border}`, borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: 20 };
const stat = (label: string, value: string, big?: boolean) => (
  <div><Label>{label}</Label><div style={{ fontFamily: 'Funnel Sans', fontSize: big ? 20 : 14, fontWeight: big ? 700 : 600, color: big ? CRC.blueprintBlue : CRC.textGrey }}>{value}</div></div>
);

export const SpreadModuleDetail: React.FC<Props> = ({ spread, moduleKey, pricing, setMachine, setMtMills, setMtOptions, setNdtSources, setCoatingSources, setPlatformOptions, setRetentionOptions, setEnabled, onEditOverrides, spreadState, weldMode, setWeldMode, mobilizing, setMobilizing }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const ln = pricing[moduleKey];
  const enabled = spread.enabled[moduleKey];
  // Every enabled module comes from the live engine; show "…" until it resolves.
  const livePending = enabled && spreadState.status !== 'done';

  const isSourceModule = moduleKey === 'mt' || moduleKey === 'ndt' || moduleKey === 'coating';
  const sk = moduleKey as SourceModule;
  const sourceSummary = (): string => {
    const meta = SOURCE_META[sk];
    const srcs = moduleSources(spread, sk);
    const base = `${srcs.length} ${meta.noun.toLowerCase()}${srcs.length === 1 ? '' : 's'} · ${moduleVolume(spread, sk).toLocaleString()} ${meta.unit}`;
    if (sk !== 'mt') return base;
    const addOns = MT_ADDONS.filter((a) => spread.mtOptions[a.key]).map((a) => a.label);
    return `${base} · ${addOns.length ? addOns.join(', ') : 'no add-ons'}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 760 }}>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontFamily: 'Funnel Sans', fontSize: 20, fontWeight: 700, color: CRC.blueprintBlue }}>{MODULE_LABELS[moduleKey]}</div>
            <div style={{ fontSize: 12, color: CRC.heritageGrey }}>{spread.name}</div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Toggle on={enabled} onClick={() => setEnabled(moduleKey, !enabled)} label={enabled ? 'Included' : 'Not included'} />
            <button onClick={onEditOverrides}
              style={{ padding: '8px 14px', border: `1px solid ${CRC.border}`, borderRadius: 8, background: '#fff', color: CRC.readableRed, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
              Edit overrides
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 28, marginTop: 16 }}>
          {stat('Margin', !enabled ? '—' : livePending ? '…' : `${Math.round(ln.margin * 100)}%`)}
          {stat('Cost', !enabled ? '—' : livePending ? '…' : money(ln.cost))}
          {stat('Price', !enabled ? 'Not included' : livePending ? '…' : money(ln.price), true)}
        </div>
      </div>

      {!enabled && (
        <div style={card}>
          <div style={{ fontSize: 13, color: CRC.textGrey }}>{MODULE_LABELS[moduleKey]} is not included in this spread. Toggle <strong>Included</strong> above to price it (its settings are kept).</div>
        </div>
      )}

      {enabled && moduleKey === 'welding' && (
        <div style={card}>
          <div style={{ fontFamily: 'Funnel Sans', fontSize: 15, fontWeight: 600, color: CRC.blueprintBlue, marginBottom: 14 }}>Machine allocation (this spread)</div>
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
            {MACHINE_KEYS.map((k) => (
              <div key={k}><Label>{MACHINE_LABELS[k]}</Label><div style={{ marginTop: 4 }}>
                <NumberInput value={spread.machines[k]} onChange={(v) => setMachine(k, Math.max(0, v))} width={84} />
              </div></div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 30, flexWrap: 'wrap', marginTop: 18, alignItems: 'center' }}>
            <div><Label>Connectivity</Label><div style={{ marginTop: 5 }}>
              <Segmented value={weldMode} onChange={(v) => setWeldMode(v as WeldMode)} options={[{ value: 'live', label: 'Connected' }, { value: 'offline', label: 'Offline (no cloud)' }]} />
            </div></div>
            <div><Label>Mobilization</Label><div style={{ marginTop: 8 }}>
              <Toggle on={mobilizing} onClick={() => setMobilizing(!mobilizing)} label={mobilizing ? 'Ship rigs to this spread' : 'Not mobilized'} />
            </div></div>
          </div>
          <div style={{ marginTop: 12, fontSize: 11.5, color: CRC.heritageGrey }}>
            Full welding rate card is in <strong>Edit overrides</strong>. Offline zeroes the internet + cloud line when the offline multiplier is 0 in Configuration. Mobilization uses Shipping cost/kg from Configuration.
          </div>
        </div>
      )}

      {enabled && moduleKey === 'welding' && <SpreadWeldingLive spread={spread} state={spreadState} mode={weldMode} />}

      {enabled && isSourceModule && (
        <>
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontFamily: 'Funnel Sans', fontSize: 15, fontWeight: 600, color: CRC.blueprintBlue }}>{moduleKey === 'mt' ? 'Data sources & add-ons' : 'Data sources'}</div>
                <div style={{ fontSize: 12, color: CRC.heritageGrey, marginTop: 3 }}>{sourceSummary()}</div>
              </div>
              <button onClick={() => setModalOpen(true)}
                style={{ padding: '8px 14px', border: `1px solid ${CRC.border}`, borderRadius: 8, background: '#fff', color: CRC.readableRed, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                Configure
              </button>
            </div>
          </div>
          <SpreadModuleLive state={spreadState} module={sk} label={MODULE_LABELS[sk]} />
          {modalOpen && (
            <SpreadModal title={`${spread.name} · ${MODULE_LABELS[sk]} sources`}
              subtitle={`Data sources (${SOURCE_META[sk].noun.toLowerCase()}s)${moduleKey === 'mt' ? ' and add-ons' : ''} priced for this spread.`}
              onClose={() => setModalOpen(false)}>
              {moduleKey === 'mt'
                ? <SpreadMaterialInputs spread={spread} joints={joints(spread)} setMills={setMtMills} setOptions={setMtOptions} />
                : <SpreadSourceInputs sources={moduleSources(spread, sk)} setSources={sk === 'ndt' ? setNdtSources : setCoatingSources} noun={SOURCE_META[sk].noun} countLabel={SOURCE_META[sk].countLabel} joints={joints(spread)} />}
            </SpreadModal>
          )}
        </>
      )}

      {enabled && (moduleKey === 'platform' || moduleKey === 'retention') && (
        <>
          <SpreadProjectOptionsCard module={moduleKey} platform={spread.platformOptions} retention={spread.retentionOptions}
            setPlatform={setPlatformOptions} setRetention={setRetentionOptions} />
          <SpreadProjectLevelLive state={spreadState} module={moduleKey} label={MODULE_LABELS[moduleKey]}
            dataModules={(['mt', 'ndt', 'coating'] as ModuleKey[]).filter((k) => spread.enabled[k]).map((k) => MODULE_LABELS[k])} />
        </>
      )}
    </div>
  );
};
